import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { TNullable } from '@common/types';
import { IMPROVED_HEADING_OFFSET } from '@constants/standard-pdf';
import {
  PDFReadingMode,
  scrollElementsInOrder,
  ScrollSource,
} from '@pages/pdf-viewer/pdf-viewer-shared-props';
import { Document, Page } from 'react-pdf';
import { TChapterInfo, TPageDimensions } from './pdf.models';
import 'react-pdf/dist/esm/Page/TextLayer.css';

interface IPDFDocumentProps {
  handlePageRenderSuccess: ({ width, height }: TPageDimensions) => void;
  originalPageWidth: number;
  originalPageHeight: number;
  onDocumentLoadSuccess: ({ numPages }: { numPages: number }) => void;
  pageDimensions: TPageDimensions;
  scale: number;
  url: string;
  selectedChapter: TNullable<TChapterInfo>;
  selectedPage: number;
  scrollSource: ScrollSource;
  setScrollSource: Dispatch<SetStateAction<ScrollSource>>;
  readingMode: PDFReadingMode;
  onVisiblePageChange?: (page: number) => void;
}

function PDFDocument(props: IPDFDocumentProps) {
  const [isFirstTime, setFirstTime] = useState<boolean>(true);
  const [numPages, setNumPages] = useState<number>(0);
  const activeChapterRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (PDFReadingMode.SinglePage === props.readingMode) {
      if (activeChapterRef.current) {
        activeChapterRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      if (ScrollSource.Chapter === props.scrollSource && activeChapterRef.current) {
        activeChapterRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        props.setScrollSource(ScrollSource.None);
      }
    }
  }, [props.selectedChapter, activeChapterRef]); // eslint-disable-line

  useEffect(() => {
    if (PDFReadingMode.ContinuousPage === props.readingMode) {
      const pageEl = pageRefs.current[props.selectedPage - 1];
      if (ScrollSource.Page === props.scrollSource && pageEl) {
        pageEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (ScrollSource.PageAndChapter === props.scrollSource) {
        scrollElementsInOrder([
          { element: pageEl, type: ScrollSource.Page },
          { element: activeChapterRef.current, type: ScrollSource.Chapter },
        ]);
      }
      props.setScrollSource(ScrollSource.None);
    }
  }, [props.selectedPage, props.scrollSource]); // eslint-disable-line

  useEffect(() => {
    if (!props.onVisiblePageChange || PDFReadingMode.SinglePage === props.readingMode) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first page that's at least 50% visible
        const visible = entries
          .filter((entry) => entry.isIntersecting && entry.intersectionRatio > 0.5)
          .map((entry) => Number(entry.target.getAttribute('data-page')));

        if (visible.length > 0) {
          props.onVisiblePageChange?.(visible[0]);
        }
      },
      {
        root: null,
        threshold: [0.5], // 50% visibility
      }
    );

    pageRefs.current.forEach((page) => {
      if (page) observer.observe(page);
    });

    return () => observer.disconnect();
  }, [numPages, props.readingMode]); // eslint-disable-line

  function handleSuccessRender({ width, height }: TPageDimensions) {
    props.handlePageRenderSuccess({ width, height });
    setTimeout(() => {
      if (activeChapterRef.current && isFirstTime) {
        activeChapterRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setFirstTime(false);
      }
    }, 300);
  }

  const scaleX = parseFloat((props.pageDimensions.width / props.originalPageWidth).toFixed(2));
  const scaleY = parseFloat((props.pageDimensions.height / props.originalPageHeight).toFixed(2));

  let activeChapter = null;
  if (props.selectedChapter) {
    activeChapter = {
      ...props.selectedChapter,
      coordinates: {
        x: (props.selectedChapter.coordinates.x + IMPROVED_HEADING_OFFSET.X) * scaleX,
        y: (props.selectedChapter.coordinates.y + IMPROVED_HEADING_OFFSET.Y) * scaleY,
      },
      width: (props.selectedChapter.width + IMPROVED_HEADING_OFFSET.WIDTH) * scaleX,
      height: (props.selectedChapter.height + IMPROVED_HEADING_OFFSET.HEIGHT) * scaleY,
    };
  }

  if (PDFReadingMode.SinglePage === props.readingMode) {
    return (
      <section className="pdf-document relative mt-4">
        <Document
          file={props.url}
          onLoadSuccess={({ numPages }) => {
            setNumPages(numPages);
            props.onDocumentLoadSuccess({ numPages });
          }}
        >
          <Page
            pageNumber={props.selectedPage}
            renderTextLayer={true}
            renderAnnotationLayer={false}
            scale={props.scale}
            onRenderSuccess={handleSuccessRender}
          />
          {activeChapter && activeChapter.page === props.selectedPage && (
            <div
              ref={activeChapterRef}
              className="highlight absolute bg-xircle-main/25 rounded-md border-b-2 border-xircle-main"
              style={{
                top: activeChapter.coordinates.y,
                left: activeChapter.coordinates.x,
                width: activeChapter.width,
                height: activeChapter.height,
              }}
            />
          )}
        </Document>
      </section>
    );
  }
  return (
    <section className="pdf-document relative mt-4">
      <Document
        file={props.url}
        onLoadSuccess={({ numPages }) => {
          setNumPages(numPages);
          props.onDocumentLoadSuccess({ numPages });
        }}
      >
        {Array.from(new Array(numPages), (_, index) => {
          const pageNumber = index + 1;
          const isActiveChapter = activeChapter && activeChapter.page === pageNumber;

          return (
            <div
              key={`page_${pageNumber}`}
              ref={(el) => {
                pageRefs.current[index] = el;
              }}
              data-page={pageNumber}
              className="relative mb-4 scroll-mt-20"
            >
              <Page
                pageNumber={pageNumber}
                renderTextLayer={true}
                renderAnnotationLayer={false}
                scale={props.scale}
                onRenderSuccess={handleSuccessRender}
              />
              {isActiveChapter && (
                <div
                  ref={activeChapterRef}
                  className="highlight absolute bg-xircle-main/25 rounded-md border-b-2 border-xircle-main"
                  style={{
                    top: activeChapter?.coordinates.y,
                    left: activeChapter?.coordinates.x,
                    width: activeChapter?.width,
                    height: activeChapter?.height,
                  }}
                />
              )}
            </div>
          );
        })}
      </Document>
    </section>
  );
}

export default PDFDocument;
